// Customer-facing label for a student's grade (students.grade).
//
// The column is a STRING and its values are not all numbers: "1".."12", plus "K"
// (kindergarten), "PS" (preschool), "ST" (staff), and "0" — which is ambiguous in the
// data and must not be guessed at.
//
// Returns null when there is nothing honest to show, and every caller is expected to
// omit the line entirely rather than print a placeholder. Showing a parent the wrong
// grade for their child is worse than showing none.
const NAMED_GRADES = {
  K: 'Kindergarten',
  PS: 'Preschool',
  TK: 'Transitional Kindergarten',
};

const ordinal = (n) => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

// Spelled out, for headlines: "Emma's Fourth Grade Portraits" reads better than "4th".
const ORDINAL_WORDS = [
  null, 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth',
  'Seventh', 'Eighth', 'Ninth', 'Tenth', 'Eleventh', 'Twelfth',
];

/**
 * @param grade  students.grade (a string: "1".."12", "K", "PS", "ST", "0")
 * @param style  'ordinal' (default) => "4th Grade"; 'word' => "Fourth Grade".
 *               ONE implementation of "what grade is this" — the two forms are a
 *               rendering choice, not a reason for a second function to drift from.
 */
export default function gradeLabel(grade, { style = 'ordinal' } = {}) {
  const raw = String(grade ?? '').trim().toUpperCase();
  if (!raw) return null;
  // Staff are photographed on the same jobs but are not students.
  if (raw === 'ST') return null;
  if (NAMED_GRADES[raw]) return NAMED_GRADES[raw];

  const n = Number(raw);
  // "0" exists in the data with no agreed meaning (pre-K? unassigned?), so it is not a
  // grade we can name. Anything outside 1-12 is likewise not a grade we recognize.
  if (!Number.isInteger(n) || n < 1 || n > 12) return null;

  return style === 'word' ? `${ORDINAL_WORDS[n]} Grade` : `${ordinal(n)} Grade`;
}

/**
 * The portraits headline: "Emma's Fourth Grade Portraits", or "Emma's Portraits" when the
 * grade isn't one we can name (staff, unknown) — never a wrong or empty grade.
 */
export function portraitsHeadline(firstName, grade) {
  const name = String(firstName || '').trim();
  if (!name) return 'Your Portraits';
  const word = gradeLabel(grade, { style: 'word' });
  return word ? `${name}'s ${word} Portraits` : `${name}'s Portraits`;
}
